import { useEffect } from "react";
import type { FC } from "react";
import { Button } from "react-bootstrap";

import { useAppSelector, useAppDispatch } from "store/hooks";
import { getTopicAssignments, setTopicAssignments } from "store/slices/assignmentNavigation";

import { useLazyGetHistoryTutoringsQuery } from "api/generated";

import { processApiError } from "utils/processApiError";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

import IconArrowLeft from "images/icons/arrow-left.svg";
import IconArrowRight from "images/icons/arrow-right.svg";
interface Props {
  userAssignmentId: string;
  tutoringId: string;
  onNavigate: (assignmentId: string) => void;
}

export const TutoringHistoryAssignmentsNavigation: FC<Props> = ({ userAssignmentId, tutoringId, onNavigate }) => {
  const assignmentNavigation = useAppSelector(getTopicAssignments(tutoringId));
  const dispatch = useAppDispatch();

  const [getAssignmentsData, { error: apiLazyError }] = useLazyGetHistoryTutoringsQuery();

  useEffect(() => {
    if (apiLazyError === undefined) return;

    processApiError(apiLazyError);
    dispatch(setTopicAssignments({ topicId: tutoringId, assignments: [] }));
  }, [apiLazyError]);

  useEffect(() => {
    if (assignmentNavigation === undefined) {
      getAssignmentsData()
        .then(response => {
          const tutoring = response.data?.userTutoringsAndSessions.find(item => item.tutoring?.id === tutoringId);

          if (!tutoring?.tutoring) return;

          const fixedIds = tutoring.tutoring.themeAssignments
            .filter(assignment => assignment.finished)
            .map(assignment => assignment.userAssignment)
            .filter(BooleanWithTypeGuard);

          const otherIds = tutoring.tutoring.assignments
            .filter(assignment => assignment.finished)
            .map(assignment => assignment.userAssignment)
            .filter(BooleanWithTypeGuard);

          dispatch(setTopicAssignments({ topicId: tutoringId, assignments: [...fixedIds, ...otherIds] }));
        })
        .catch(error => {
          console.error(error);
          dispatch(setTopicAssignments({ topicId: tutoringId, assignments: [] }));
        });
    }
  }, [assignmentNavigation]);

  const navigatePrevious = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index === 0) {
      onNavigate(assignmentNavigation[assignmentNavigation.length - 1]);
      return;
    }

    onNavigate(assignmentNavigation[index - 1]);
  };

  const navigateNext = () => {
    if (assignmentNavigation === undefined || assignmentNavigation.length <= 1) return;

    const index = assignmentNavigation.indexOf(userAssignmentId);

    if (index === -1) return;
    if (index >= assignmentNavigation.length - 1) {
      onNavigate(assignmentNavigation[0]);
      return;
    }

    onNavigate(assignmentNavigation[index + 1]);
  };

  return (
    <div className="assignment-navigation">
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigatePrevious}>
        <IconArrowLeft />
      </Button>
      <Button variant={"select"} className={"ms-4px btn-shadow"} onClick={navigateNext}>
        <IconArrowRight />
      </Button>
    </div>
  );
};
